import {
  EditOutlined,
  FileDownloadOutlined,
  PersonAddOutlined,
  RemoveCircleOutline,
  VisibilityOutlined,
  WestOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { isArray, isEmpty, isNil } from "lodash";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDrilldownBaseUrl } from "../../../../../pipelines/PipelineContext";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../../_src_shared/components/layout/PrimaryContentCard";
import OverviewItem from "../../../../../components/data/OverviewItem";
import SelectInput from "../../../../../components/inputs/SelectInput";
import SelectToggleInput from "../../../../../components/inputs/SelectToggleInput";
import Flx from "../../../../../components/layout/Flx";
import Htag from "../../../../../components/typography/Htag";
import Txt from "../../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanTermsHook } from "../../../../../hooks/useLoanTermsHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../../redux/actions/sidebarActions";
import { loanFetchedDataLoanTermsSet } from "../../../../../redux/reducers/loanFetchedDataReducer";
import { camelCaseToSentence } from "../../../../../utils/strings/camelCaseToSentence";
import LoanDrilldownButtonAddCredit from "../../../buttons/LoanDrilldownButtonAddCredit";
import LoanDrilldownButtonAddFee from "../../../buttons/LoanDrilldownButtonAddFee";

import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import InsetCard from "../../../../../components/ui/InsetCard";
import LoanDrilldownButtonAddSpecialCondition from "../../../buttons/LoanDrilldownButtonAddSpecialCondition";
import OriginatorCreditsTable from "../tables/OriginatorCreditsTable";
import OriginatorFeesTable from "../tables/OriginatorFeesTable";
import UpdateLoanRootDataFieldsModal from "../../../../../components/common/Loan/UpdateLoanRootDataFieldsModal";
import { usePdfDownloadHook } from "../../../../../hooks/usePdfDownloadHook";
import PdfPreviewer from "../../../../../components/pdf/components/PdfPreviewer";
import LoanTermsSheetPDF from "../../../../../components/pdf/LoanTermsSheetPDF/LoanTermsSheetPDF";
import { addressLabel } from "../../../../../_src_shared/utils/functions/addressLabel";

const LoanFeesSubTabTermsSheet = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const [showPreview, setShowPreview] = useState(false);
  const headerSubtitle = useMemo(() => {
    const loanNumber = loanDrilldown?.loanNumber || "XXXXXX";
    const propertyAddress = !isArray(loanDrilldown?.subjectProperties)
      ? ""
      : addressLabel(loanDrilldown?.subjectProperties[0]?.address);
    // const propertyAddress = "3112 North Stillman Street";
    return `Loan #${loanNumber} - ${propertyAddress}`;
  }, [loanDrilldown]);
  return (
    <PrimaryContentCard
      maxWidth="lg"
      title="Loan Terms Sheet"
      endContent={
        <Flx ac gap={1}>
          <Button
            variant="outlined"
            startIcon={
              showPreview ? (
                <WestOutlined className="thin" />
              ) : (
                <VisibilityOutlined className="thin" />
              )
            }
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <DownloadLoanTermsButton
            data={{ guarantors: loanDrilldown?.borrowers }}
          />
        </Flx>
      }
      subTitle={headerSubtitle}
      loading={isNil(loanTerms)}
    >
      {showPreview ? (
        <PdfPreviewer sx={{ height: "78vh", width: "100%" }}>
          <LoanTermsSheetPDF
            formData={{ guarantors: loanDrilldown?.borrowers }}
          />
        </PdfPreviewer>
      ) : (
        <Flx column g={2}>
          <FundingEntitySection />
          <LoanTermsSection />
          <OriginatorFeesSection />
          <OriginatorCreditsSection />
          {/* <EscrowItemsSection /> */}
          {/* <NetAmountSection /> */}
        </Flx>
      )}
    </PrimaryContentCard>
  );
};

const DownloadLoanTermsButton = memo(({ data }) => {
  // const { values } = useFormState();

  const { downloadPDF } = usePdfDownloadHook({
    formData: data,
    filename: "loan-terms-sheet-WIP.pdf",
    type: "loan-terms-sheet",
  });

  return (
    <Button
      sx={{ flexGrow: 1 }}
      size="small"
      variant="contained"
      startIcon={<FileDownloadOutlined className="thin" />}
      onClick={downloadPDF}
    >
      Download (PDF)
    </Button>
  );
});

const FundingEntitySection = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const fundingEntities = useSelector(
    (state) => state?.loanFetchedData?.fundingEntities
  );
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const dispatch = useDispatch();
  const fundingEntityId = useSelector(
    (state) => state?.loanFetchedData?.loanTerms?.fundingEntity?._id
  );

  const { loading, updateFundingEntity } = useLoanTermsHook();

  const handleOnContactChange = useCallback(() => {
    dispatch(
      sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT)
    );
    dispatch(sidebarSetStateValue(null));
  }, [dispatch]);

  const handleOnFundingEntityChange = useCallback(
    (v) => {
      const fullData = fundingEntities.find((contact) => contact._id === v);

      if (isNil(fullData)) {
        return;
      }

      updateFundingEntity({
        dealId: loanDrilldown?._id,
        data: fullData,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataLoanTermsSet(data));
        },
      });
    },
    [fundingEntities, loanDrilldown?._id]
  );

  const entityOptions = useMemo(() => {
    return fundingEntities?.map((e) => ({
      value: e?._id,
      label: e?.name,
    }));
  }, [fundingEntities]);

  return (
    <InsetCard>
      {loading && isNil(fundingEntities) ? (
        <Flx center column gap={1} sx={{ py: 5 }}>
          <CircularProgress size={18} />
          <Txt>Loading funding entities...</Txt>
        </Flx>
      ) : (
        <FlxGrid width="300px" gap={8}>
          <Flx column jb gap={4}>
            <Flx column>
              <Txt bold>
                {isNil(fundingEntityId)
                  ? "Assign Funding Entity"
                  : "Funding Entity"}
              </Txt>
              <SelectToggleInput
                disabled={loading}
                value={fundingEntityId}
                onChange={handleOnFundingEntityChange}
                // onChange={(val) => setFundingEntityId(val)}
                options={entityOptions}
                placeholder="Select Funding Entity"
              />
            </Flx>
            <FlxGrid width="300px" gap={2.4} sx={{ mt: 2 }}>
              <Flx ac gap={1} sx={{ flexGrow: 1, alignItems: "flex-end" }}>
                <LoanTermsTitleContactDropdown />
                <Flx ac>
                  {isNil(loanTerms?.titleContact) ? null : (
                    <LoanTermsUnassignTitleContactIconButton />
                  )}
                  <Tooltip title="Add Title Contact">
                    <IconButton onClick={handleOnContactChange}>
                      <PersonAddOutlined className="thin" />
                    </IconButton>
                  </Tooltip>
                </Flx>
              </Flx>
            </FlxGrid>
          </Flx>
          <Flx column>
            <Txt bold>Mortgage Clause</Txt>
            <InsetCard grey sx={{ p: 1.5 }}>
              <FundingEntityAddress
                fundingEntity={fundingEntities?.find(
                  (opt) => opt._id === fundingEntityId
                )}
              />
            </InsetCard>
          </Flx>
        </FlxGrid>
      )}
    </InsetCard>
  );
};

const FundingEntityAddress = ({ fundingEntity }) => {
  if (isNil(fundingEntity)) {
    return (
      <pre style={{ lineHeight: "24px" }}>
        Select a funding entity to view clause.
      </pre>
    );
  }
  return (
    <Box
      sx={{
        lineHeight: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        ".txt": {
          fontFamily: "monospace !important",
          fontSize: "12px",
        },
      }}
    >
      <Txt>{fundingEntity?.shortName}</Txt>
      <Txt>{fundingEntity?.addressLine1}</Txt>
      <Txt>{fundingEntity?.addressLine2}</Txt>
      <Txt>{fundingEntity?.city}</Txt>
      <Txt>{fundingEntity?.state}</Txt>
      <Txt>{fundingEntity?.zipCode}</Txt>
    </Box>
  );
};

const LoanTermsSection = () => {
  const [show, setShow] = useState(true);

  const fundingShieldFields = useMemo(
    () => [
      {
        name: "fundingShieldStatus",
        label: "Funding Shield Status",
        type: "select",
        options: ["Not Submitted", "Submitted", "Received"],
      },
      {
        name: "fundingShieldRequestedDate",
        label: "Funding Shield Requested Date",
        type: "date",
      },
    ],
    []
  );

  const termsFields = useMemo(
    () => [
      {
        name: "termsAccepted",
        label: "Terms Accepted?",
        type: "booleanToggle",
      },
      {
        name: "termsAcceptedDate",
        label: "Terms Accepted Date",
        type: "date",
      },
    ],
    []
  );
  const closingFields = useMemo(
    () => [
      {
        name: "closingStatus",
        label: "Closing Status",
        type: "select",
        options: [
          "An issue with the file",
          "Clearing Post Closing Conditions",
          "Documents drafted",
          "Engage Counsel",
          "Executed Loan Docs Received and Under Review",
          "Expecting incoming wire",
          "Funded",
          "Loan docs need to be drafted",
          "Loan docs out for execution",
          "Opened with Counsel",
          "P/C Collateral Cleared",
          "P/C Pending Scans",
          "P/C Scans Received and Under Review",
          "Ready for QC Review",
          "Ready to request wire",
          "Requested wire",
          "Settlement Statement Under Review",
          "SS out for signature",
          "Waiting for incoming wire",
        ],
      },
      {
        name: "closingScheduled",
        label: "Closing Scheduled?",
        type: "booleanToggle",
      },
      {
        name: "expectedClosingDate",
        label: "Expected Closing Date",
        type: "date",
        size: 6,
      },
      {
        name: "closingScheduledDate",
        label: "Closing Scheduled Date",
        type: "date",
        size: 6,
      },
    ],
    []
  );
  return (
    <InsetCard grey>
      <Flx
        fw
        jb
        ac
        sx={{
          // mb: 2,
          mb: 1,

          //
        }}
      >
        <Htag variant="h2">Loan Terms</Htag>
        {/* <Tooltip
          title="Edit Loan Terms Details"
          onClick={() =>
            navigate(`/loan/${loanDrilldown?._id}/loan-information`)
          }
        >
          <IconButton size="small">
            <EditOutlined className="thin" />
          </IconButton>
        </Tooltip> */}
      </Flx>
      <Flx
        column
        gap={4}
        // gap={3}
        // gap={2.4}
        // gap={2.4}
        sx={{
          mt: 3,
          ".MuiFormControl-root": {
            background: "#fff",
          },
        }}
      >
        <FlxGrid grow={1} gap={2}>
          <LoanTermsGroup
            //
            editFields={termsFields}
            title="Terms"
          >
            <LoanTermsDisplayData name={"termsAccepted"} type={"checkbox"} />
            <LoanTermsDisplayData name={"termsAcceptedDate"} />
          </LoanTermsGroup>

          <LoanTermsGroup
            title="Funding Shield"
            editFields={fundingShieldFields}
          >
            <LoanTermsDisplayData name={"fundingShieldStatus"} />
            <LoanTermsDisplayData
              name={"fundingShieldRequestedDate"}
              type={"date"}
            />
          </LoanTermsGroup>
        </FlxGrid>

        <LoanTermsGroup title="Closing" editFields={closingFields}>
          <LoanTermsDisplayData name={"closingStatus"} />
          <LoanTermsDisplayData name={"closingScheduled"} type={"checkbox"} />
          <LoanTermsDisplayData name={"expectedClosingDate"} type={"date"} />
          <LoanTermsDisplayData name={"closingScheduledDate"} />
        </LoanTermsGroup>
        <LoanTermsGroup
          title="Wire Instructions"
          size="xs"
          onNavigate={`fees-and-closing/wire-instructions`}
        >
          <LoanTermsDisplayData
            dataKey="wireInstructions"
            name={"wireStatus"}
          />
          <LoanTermsDisplayData
            dataKey="wireInstructions"
            name={"wireRequestDate"}
            type={"date"}
          />
          <LoanTermsDisplayData
            dataKey="wireInstructions"
            name={"closingWireReleaseDate"}
            type={"date"}
          />
          <LoanTermsDisplayData
            dataKey="wireInstructions"
            name={"wireAmount"}
            type={"dollar"}
          />
          <LoanTermsDisplayData
            dataKey="wireInstructions"
            name={"wireFedRefNumber"}
          />
          <LoanTermsDisplayData dataKey="wireInstructions" name={"wireNotes"} />
        </LoanTermsGroup>

        <Divider flexItem>
          <Button
            onClick={() => setShow(!show)}
            size="small"
            sx={{
              color: grey[600],
              border: `1px solid ${grey[300]}`,
              background: show ? grey[200] : "transparent",
            }}
            variant="text"
            color="grey"
          >
            {show ? "Show Less" : "Show More"}
          </Button>
        </Divider>
        {show && (
          <>
            <LoanTermsGroup title="Totals">
              <LoanTermsDisplayData name={"totalBrokerTerms"} type={"dollar"} />
              <LoanTermsDisplayData
                name={"totalCorrespondentFees"}
                type={"dollar"}
              />
              <LoanTermsDisplayData name={"totalEscrowItems"} type={"dollar"} />
              <LoanTermsDisplayData name={"totalHoldback"} type={"dollar"} />
              <LoanTermsDisplayData
                name={"totalInterestReserves"}
                type={"dollar"}
              />
              <LoanTermsDisplayData name={"totalLoanAmount"} type={"dollar"} />
              <LoanTermsDisplayData
                name={"totalOriginatorCredits"}
                type={"dollar"}
              />
              <LoanTermsDisplayData
                name={"totalOriginatorFees"}
                type={"dollar"}
              />
              <LoanTermsDisplayData
                name={"totalThirdPartyFees"}
                type={"dollar"}
              />
            </LoanTermsGroup>

            <LoanTermsGroup title="Derived From Loan">
              <LoanTermsDisplayData name={"interestRate"} type={"percent"} />
              <LoanTermsDisplayData name={"interestCalcType"} />
              <LoanTermsDisplayData name={"interestReserves"} type={"dollar"} />
              <LoanTermsDisplayData name={"dutchInterest"} type={"checkbox"} />
              <LoanTermsDisplayData name={"totalHoldback"} type={"dollar"} />
              <LoanTermsDisplayData
                name={"originationPoints"}
                type={"percent"}
              />
              <LoanTermsDisplayData name={"discountPoints"} type={"percent"} />
              <LoanTermsDisplayData
                label={"Term Months"}
                name={"termMonths"}
                type={"number"}
              />
            </LoanTermsGroup>

            <LoanTermsGroup title="Other">
              <LoanTermsDisplayData name={"firstPaymentDate"} type={"date"} />
              <LoanTermsDisplayData name={"maturityDate"} type={"date"} />

              <LoanTermsDisplayData name={"buyRate"} />
              <LoanTermsDisplayData name={"dscrBuyPrice"} />
            </LoanTermsGroup>
          </>
        )}
        <SpecialTermsContent />
      </Flx>
    </InsetCard>
  );
};

const LoanTermsGroup = ({
  children,
  title,
  editFields,
  size = "xs",
  onNavigate,
}) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const baseUrl = useDrilldownBaseUrl(loanDrilldown?._id);

  const onEditClick = useCallback(() => {
    if (editFields) {
      setShow(true);
    }

    if (onNavigate) {
      navigate(`${baseUrl}/${onNavigate}`);
    }
  }, [editFields, onNavigate, baseUrl]);
  return (
    <>
      <UpdateLoanRootDataFieldsModal
        show={show}
        maxWidth={size}
        onClose={() => setShow(false)}
        fields={editFields}
      >
        {children}
      </UpdateLoanRootDataFieldsModal>
      <Flx column gap={"3px"}>
        {title && (
          <>
            <Txt sx={{ fontWeight: 300, color: grey[800] }} fontSize={12}>
              {title}
            </Txt>
          </>
        )}
        <Box
          sx={{
            position: "relative",
            border: "1px solid #e5e7eb",
            p: 2.5,
            background: "#ffffff",
            borderRadius: 1.5,
          }}
        >
          <FlxGrid
            grow={1}
            width="165px"
            gap={3}
            // sx={{
            //   position: "relative",
            //   border: "1px solid #e5e7eb",
            //   p: 2.5,
            //   background: "#ffffff",
            //   borderRadius: 1.5,
            // }}
          >
            {(editFields || onNavigate) && (
              <Tooltip title="Edit Details">
                <IconButton
                  size="small"
                  onClick={onEditClick}
                  sx={{ position: "absolute", top: 4, right: 4 }}
                >
                  <EditOutlined className="thin" sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {children}
          </FlxGrid>
        </Box>
      </Flx>
    </>
  );
};

const SpecialTermsContent = ({ children }) => {
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);

  const textBasedTerms = useMemo(
    () => loanTerms?.textBasedTerms || [],
    [loanTerms?.textBasedTerms]
  );

  return (
    <>
      <Flx column gap={1} sx={{ flexGrow: 1, mt: 3 }}>
        {isEmpty(textBasedTerms) ? null : (
          // <Txt bold>Special Terms & Conditions</Txt>
          <Txt sx={{ fontWeight: 300, color: grey[800] }} fontSize={12}>
            Special Terms & Conditions
          </Txt>
        )}

        <SpecialConditionRenderer conditions={textBasedTerms} />
        <LoanDrilldownButtonAddSpecialCondition />
      </Flx>
    </>
  );
};

const SpecialConditionRenderer = ({ conditions }) => {
  return (
    <Flx column gap={2} sx={{ mb: 1 }}>
      {conditions?.map((c) => (
        <SpecialConditionItem key={c.id} condition={c} />
      ))}
    </Flx>
  );
};

const SpecialConditionItem = ({ condition }) => {
  const dispatch = useDispatch();
  const onRowClicked = useCallback((d) => {
    dispatch(
      sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_SPECIAL_CONDITION)
    );
    dispatch(sidebarSetStateValue(condition));
  }, []);

  return (
    <InsetCard
      grey
      sx={{
        px: 1,
        py: 0,
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flx fw jb ac gap={2} sx={{ px: 1, py: 0.8, pt: 1.2 }}>
        <Txt fontSize={10} bold>
          {formatDateTime(condition.createdDate)}
        </Txt>
        <Button
          size="small"
          variant="text"
          onClick={onRowClicked}
          sx={{ fontSize: "10px" }}
        >
          Edit Condition
        </Button>
      </Flx>
      <Divider flexItem />
      <Flx sx={{ flexGrow: 1, px: 1, py: 2 }}>
        <MultilineDisplay>
          {condition.termValue || "No special condition details provided."}
        </MultilineDisplay>
      </Flx>
    </InsetCard>
  );
};

const LoanTermsTitleContactDropdown = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanRelatedContacts = loanDrilldown?.loanRelatedContacts || [];
  const { loading, updateTitleContact } = useLoanTermsHook();
  const dispatch = useDispatch();
  const titleContactId = useSelector(
    (state) => state?.loanFetchedData?.loanTerms?.titleContact?._id
  );

  const handleOnChange = (v) => {
    const contactData = loanRelatedContacts.find(
      (contact) => contact._id === v
    );

    updateTitleContact({
      dealId: loanDrilldown?._id,
      data: contactData,
      // data: contactData,
      onSuccessFn: (data) => {
        dispatch(loanFetchedDataLoanTermsSet(data));
      },
    });
    return;
  };

  return (
    <LoanContactsDropdown
      loading={loading}
      label={isNil(titleContactId) ? "Assign Title Contact" : "Title Contact"}
      value={titleContactId}
      onChange={handleOnChange}
    />
  );
};

const LoanTermsUnassignTitleContactIconButton = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const { loading, updateTitleContact } = useLoanTermsHook();
  const dispatch = useDispatch();

  const handleOnClick = (v) => {
    updateTitleContact({
      dealId: loanDrilldown?._id,
      data: null,
      // data: contactData,
      onSuccessFn: (data) => {
        dispatch(loanFetchedDataLoanTermsSet(data));
      },
    });
    return;
  };

  return (
    <Tooltip title="Unassign Title Contact">
      <IconButton loading={loading} onClick={handleOnClick}>
        <RemoveCircleOutline className="thin" />
      </IconButton>
    </Tooltip>
  );
};

const LoanTermsDisplayData = ({ label, value, dataKey, type, name }) => {
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const val = useMemo(() => {
    const targetData = isNil(dataKey) ? loanTerms : loanTerms?.[dataKey];
    if (value) {
      return value;
    }
    return targetData?.[name];
  }, [name, value, loanTerms, dataKey]);

  const labelValue = useMemo(() => {
    if (isNil(label)) {
      // Convert camelCase name to Title Case label
      return camelCaseToSentence(name);
    }
    return label;
  }, [label, name]);
  return (
    <OverviewItem
      label={labelValue}
      type={type}
      value={val}
      sx={{ flexGrow: 1 }}
    />
  );
};

const OriginatorFeesSection = () => {
  const originatorFees = useSelector(
    (state) => state?.loanFetchedData?.loanTerms?.originatorFees
  );
  const [rowData, setRowData] = useState();

  const dispatch = useDispatch();

  const onRowClicked = useCallback((d) => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_FEE));
    dispatch(sidebarSetStateValue(d));
  }, []);

  useEffect(() => {
    setRowData(originatorFees || []);
  }, [originatorFees]);
  return (
    <Flx column gap={1} sx={{ mt: 4 }}>
      <Flx fw jb ac>
        <Flx column>
          <Htag variant="h3">Originator Fees</Htag>
          <Txt secondary>
            Click any of the following rows to edit or delete a fee
          </Txt>
        </Flx>
        <LoanDrilldownButtonAddFee />
      </Flx>

      <InsetCard sx={{ p: 0, overflow: "hidden" }}>
        <OriginatorFeesTable onRowClicked={onRowClicked} rowData={rowData} />
      </InsetCard>
    </Flx>
  );
};

const OriginatorCreditsSection = () => {
  const originatorCredits = useSelector(
    (state) => state?.loanFetchedData?.loanTerms?.originatorCredits
  );
  const [rowData, setRowData] = useState(null);

  const dispatch = useDispatch();

  const onRowClicked = useCallback((d) => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_CREDIT));
    dispatch(sidebarSetStateValue(d));
  }, []);

  useEffect(() => {
    setRowData(originatorCredits || []);
  }, [originatorCredits]);
  return (
    <Flx column gap={1} sx={{ mt: 4 }}>
      <Flx fw jb ac>
        <Flx column>
          <Htag variant="h3">Originator Credits</Htag>
          <Txt secondary>
            Click any of the following rows to edit or delete a credit
          </Txt>
        </Flx>

        <LoanDrilldownButtonAddCredit />
      </Flx>

      <InsetCard sx={{ p: 0, overflow: "hidden" }}>
        <OriginatorCreditsTable
          onRowClicked={onRowClicked}
          onDeleteClick
          rowData={rowData}
        />
      </InsetCard>
    </Flx>
  );
};

const MultilineDisplay = ({ children }) => {
  return (
    <Typography sx={{ whiteSpace: "pre-line", fontSize: "11px" }}>
      {children}
    </Typography>
  );
};

/**
 * COMPONENTS
 */

const LoanContactsDropdown = ({
  label,
  value,
  onChange,
  placeholder = "Select loan contact...",
  loading,
  fullWidth = true,
  sx,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // const [selectedContact, setSelectedContact] = useState(null);

  const loanContactOptions = useMemo(() => {
    return loanDrilldown?.loanRelatedContacts?.map((contact) => {
      return {
        label: `${contact?.firstName} ${contact?.lastName} <${contact?.emailAddress || "Unknown Email"}>`,
        value: contact?._id,
      };
    });
  }, [loanDrilldown?.loanRelatedContacts]);
  return (
    // <InputWrapper label={label} sx={{ flexGrow: 1 }}>
    <Flx column sx={{ flexGrow: 1, position: "relative" }}>
      <Txt bold>{label}</Txt>
      {/* <Box sx={{ position: "relative" }}> */}
      {loading && (
        <CircularProgress
          size={14}
          sx={{
            width: "calc(100% - 4px)",
            position: "absolute",
            // height: "2px",
            zIndex: 1111,
            borderRadius: "16px",
            top: "55%",
            transform: "translateY(-50%)",
            right: 30,
            // left: 0,
          }}
        />
      )}
      <SelectInput
        fullWidth={fullWidth}
        sx={sx}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={loanContactOptions}
      />
      {/* </Box> */}
    </Flx>
    // </InputWrapper>
  );
};

export default LoanFeesSubTabTermsSheet;
